// Recommended work (approve / decline), "declined last time" banner, and the AI wording helper — used by office and phone.
import { useRef, useState } from 'react';
import { api, useApi, dollars, toCents, centsInput, uploadPhoto, photoSrc, mayI, type Row } from '../lib';

/** AI buttons only show once AI is connected (Anthropic key or Workers AI). */
export function useAiOn() { return !!useApi<{ enabled: boolean }>('/api/ai/status').data?.enabled; }

const day = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
import { Badge, Button, Icon, Modal, Form, MoneyField, Select, useToast, confirmAction } from '../ui';

const HOW_OPTS: [string, string][] = [['verbal', 'In person'], ['phone', 'By phone'], ['text', 'By text'], ['email', 'By email']];
/** Owner always; a manager with "advised photos" on; mechanics never. */
const photoBoss = () => mayI('advised_photos');

/** Photos on an advised item: 👁 shows / hides it for the customer, × deletes it (only who may); anyone can add. */
function RecPhotos({ r, woId, office, canAdd, reload }: { r: Row; woId: string; office: boolean; canAdd: boolean; reload: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const list = (r.photo_ids || []) as string[], pub = (r.public_ids || []) as string[];
  const boss = office && photoBoss();
  if (!list.length && !canAdd) return null;
  return (
    <div className="rec-photos">
      {list.map(id => (
        <span key={id} className={'rp-item' + (pub.includes(id) ? ' shown' : '')}>
          <a href={photoSrc(id)} target="_blank" rel="noreferrer"><img src={photoSrc(id)} alt="" loading="lazy" /></a>
          {office && <small>{pub.includes(id) ? 'Customer sees it' : 'Shop only'}</small>}
          {boss && <span className="rp-btns">
            <button type="button" title={pub.includes(id) ? 'Hide from the customer' : 'Show to the customer'} onClick={async () => { try { await api(`/api/recommendations/${r.id}/photos/${id}`, { method: 'PUT', body: { public: !pub.includes(id) } }); reload(); } catch (e) { toast((e as Error).message, 'err'); } }}>{pub.includes(id) ? 'Hide' : 'Show'}</button>
            <button type="button" title="Delete photo" onClick={async () => { if (!confirmAction('Delete this photo for good?')) return; try { await api(`/api/recommendations/${r.id}/photos/${id}`, { method: 'DELETE' }); reload(); } catch (e) { toast((e as Error).message, 'err'); } }}>×</button>
          </span>}
        </span>
      ))}
      {canAdd && <>
        <input ref={ref} type="file" accept="image/*" capture="environment" hidden onChange={async e => {
          const f = e.target.files?.[0]; e.target.value = ''; if (!f) return;
          setBusy(true);
          try { const { id } = await uploadPhoto(f, { kind: 'job', work_order_id: woId, note: `Advised: ${String(r.description).slice(0, 60)}` }); await api(`/api/recommendations/${r.id}/photos`, { body: { photo_id: id } }); reload(); }
          catch (err) { toast((err as Error).message, 'err'); } finally { setBusy(false); }
        }} />
        <button type="button" className="rp-add" disabled={busy} onClick={() => ref.current?.click()}><Icon name="camera" size={16} />{busy ? 'Uploading…' : 'Photo'}</button>
        <button type="button" className="rp-add" disabled={busy} title="Choose from the gallery" onClick={() => { if (ref.current) { ref.current.removeAttribute('capture'); ref.current.click(); setTimeout(() => ref.current?.setAttribute('capture', 'environment'), 1000); } }}>🖼</button>
      </>}
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = { open: 'Waiting for customer', approved: 'Customer wants it', declined: 'Customer declined' };
const STATUS_TONE: Record<string, string> = { open: 'amber', approved: 'green', declined: 'red' };

export function EarlierRecommendations({ list, office }: { list: Row[]; office: boolean }) {
  if (!list?.length) return null;
  return (
    <div className="rec-earlier">
      <b><Icon name="alert" size={15} /> Recommended on earlier visits, not done yet</b>
      <ul>{list.map(r => <li key={r.id}>{r.description}{office && r.estimate ? ` — est. ${dollars(r.estimate)}` : ''} <small>({r.status === 'declined' ? 'declined' : 'not decided'} · {r.wo_number} · {day(r.decided_at || r.created_at)})</small></li>)}</ul>
    </div>
  );
}

export function RecommendationList({ woId, list, office, locked, myId, reload }: { woId: string; list: Row[]; office: boolean; locked: boolean; myId: string; reload: () => void }) {
  const [modal, setModal] = useState<null | 'add' | 'advised' | { edit: Row }>(null);
  const toast = useToast();
  async function setStatus(r: Row, status: string) {
    try { await api(`/api/recommendations/${r.id}`, { method: 'PATCH', body: { status } }); reload(); toast(status === 'declined' ? 'Recorded: customer declined' : status === 'approved' ? 'Recorded: customer approved' : 'Reopened'); }
    catch (e) { toast((e as Error).message, 'err'); }
  }
  return (
    <>
      {list.length ? (
        <ul className="rec-list">
          {list.map(r => (
            <li key={r.id}>
              <span className="grow"><b>{r.description}</b>
                <small>{office && (r.estimate ? `Estimate ${dollars(r.estimate)} · ` : 'Price to follow · ')}{r.note ? r.note + ' · ' : ''}by {r.created_by_name}{r.decided_by_name ? ` · ${r.status} by ${r.decided_by_name} ${day(r.decided_at)}` : ''}{r.status === 'declined' && r.told_by_name ? ` · customer told by ${r.told_by_name} (${(HOW_OPTS.find(h => h[0] === r.told_how) || ['', 'in person'])[1].toLowerCase()})` : ''}{r.ack_name ? ` · signed by ${r.ack_name}` : ''}</small>
                <RecPhotos r={r} woId={woId} office={office} canAdd={!locked && (office || r.created_by === myId)} reload={reload} /></span>
              <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
              {!locked && (office || (r.created_by === myId && r.status === 'open')) && (
                <span className="row-actions">
                  {office && r.status !== 'declined' && <Button kind="ghost" icon="plus" title="Add it as a job on this work order — then add labor and parts and ask the customer to approve" onClick={async () => {
                    try { await api(`/api/recommendations/${r.id}/to-job`, { body: {} }); toast('Added as a job — add labor and parts, then Request approval'); reload(); } catch (e) { toast((e as Error).message, 'err'); }
                  }}>Make it a job</Button>}
                  {office && r.status !== 'approved' && <Button kind="ghost" onClick={() => setStatus(r, 'approved')}>Approved</Button>}
                  {office && r.status !== 'declined' && <Button kind="ghost" onClick={() => setStatus(r, 'declined')}>Declined</Button>}
                  {office && r.status !== 'open' && <Button kind="ghost" onClick={() => setStatus(r, 'open')}>Undo</Button>}
                  {office && <button className="icon-btn" title="Edit" onClick={() => setModal({ edit: r })}><Icon name="edit" size={16} /></button>}
                  <button className="icon-btn" title="Remove" onClick={async () => { if (confirmAction('Remove this recommendation?')) { await api(`/api/recommendations/${r.id}`, { method: 'DELETE' }); reload(); } }}><Icon name="trash" size={16} /></button>
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : <p className="muted pad small">{office ? 'Found something else that needs fixing? Add it here with a price. If the customer says no, mark it Declined — it prints on the invoice.' : 'Found something else that needs fixing? Add it — the office will price it and ask the customer.'}</p>}
      {!locked && <div className="pad rec-add-row">
        <Button icon="plus" className={office ? '' : 'full'} onClick={() => setModal('add')}>Recommend repair</Button>
        <Button icon="alert" className={'advised-btn' + (office ? '' : ' full')} onClick={() => setModal('advised')}>Advised, customer declined</Button>
      </div>}
      {modal && <RecModal woId={woId} office={office} advised={modal === 'advised'} edit={modal === 'add' || modal === 'advised' ? null : modal.edit} onClose={() => setModal(null)} onDone={() => { setModal(null); reload(); }} />}
    </>
  );
}

/** Add / edit a recommendation. ✨ fixes the wording (any language → shop English). No price yet → "Price to follow". */
function RecModal({ woId, office, edit, advised, onClose, onDone }: { woId: string; office: boolean; edit: Row | null; advised?: boolean; onClose: () => void; onDone: () => void }) {
  const [desc, setDesc] = useState(String(edit?.description || ''));
  const [note, setNote] = useState(String(edit?.note || ''));
  const [files, setFiles] = useState<File[]>([]);
  const team = useApi<Row[]>(office ? '/api/users' : null);
  const declined = advised || edit?.status === 'declined';
  return (
    <Modal title={edit ? 'Edit recommendation' : advised ? 'Advised — customer declined' : 'Recommend repair'} onClose={onClose}>
      <Form onCancel={onClose} submitLabel={advised ? 'Save — prints on the invoice' : 'Save'} onSubmit={async v => {
        const photo_ids: string[] = [];
        for (const f of files) photo_ids.push((await uploadPhoto(f, { kind: 'job', work_order_id: woId, note: `Advised: ${desc.slice(0, 60)}` })).id);
        const body = { description: desc, note, ...(office ? { estimate: toCents(v.estimate) } : {}), ...(declined ? { told_how: v.told_how, ...(office && v.told_by ? { told_by: v.told_by } : {}) } : {}), ...(advised ? { declined: true, photo_ids } : {}) };
        if (!edit) await api(`/api/work-orders/${woId}/recommendations`, { body });
        else await api(`/api/recommendations/${edit.id}`, { method: 'PATCH', body });
        onDone();
      }}>
        {advised && <p className="form-note full">Something you saw and told the customer about, and they said no (e.g. worn tires, no time today). It goes on the invoice under <b>Advised — customer declined</b> with today's date — your record if it fails on the road later.</p>}
        <label className="field full"><span>{advised ? 'What you found / advised *' : 'What needs to be done *'}</span>
          <span className="ai-line-row"><input name="description" value={desc} onChange={e => setDesc(e.target.value)} required placeholder={advised ? 'e.g. 3 trailer tires worn / cracked — replacement advised' : 'e.g. Replace EGR valve'} maxLength={500} /><AiLineButton text={desc} onText={setDesc} /></span></label>
        {declined && office && <Select label="Who told the customer" name="told_by" defaultValue={edit?.told_by || ''}><option value="">Me</option>{(team.data || []).filter(u => u.active !== 0).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</Select>}
        {declined && <Select label="How" name="told_how" defaultValue={edit?.told_how || 'verbal'}>{HOW_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>}
        {advised && <label className="field full"><span>Photos (optional — the best proof)</span>
          <input type="file" accept="image/*" multiple onChange={e => setFiles(Array.from(e.target.files || []).slice(0, 6))} />
          <small>{office ? (photoBoss() ? 'You choose which photos the customer sees (Show / Hide on each photo).' : 'The owner decides which photos the customer sees.') : 'The office decides which photos the customer sees.'}</small></label>}
        {office && <MoneyField label="Estimate (parts + labor)" name="estimate" defaultValue={edit ? centsInput(edit.estimate) : ''} hint="Leave empty if you don't have the parts price yet — it shows “Price to follow”." />}
        <label className="field full"><span>Why / notes</span>
          <span className="ai-line-row"><textarea name="note" value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="e.g. Valve sticking, code P0401 active" maxLength={1000} /><AiLineButton text={note} onText={setNote} /></span></label>
      </Form>
    </Modal>
  );
}

type ThreeC = { complaint: string; cause: string; correction: string };

/** "Write it properly": AI turns rough notes (any language) into clean 3C wording. The user sees it before anything changes. */
export function AiWordingButton({ values, unit, recommendations, onApply, only, className = '' }: {
  values: ThreeC; unit?: string; recommendations?: Row[]; onApply: (v: Partial<ThreeC>) => void; only?: (keyof ThreeC)[]; className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState<ThreeC | null>(null);
  const toast = useToast();
  const keys = only || (['complaint', 'cause', 'correction'] as const);
  const on = useAiOn();
  async function run() {
    setBusy(true);
    try { setOut(await api<ThreeC>('/api/ai/wording', { body: { ...values, unit, recommendations: (recommendations || []).map(r => ({ description: r.description, status: r.status })) } })); }
    catch (e) { toast((e as Error).message, 'err'); }
    finally { setBusy(false); }
  }
  return (
    <>
      {on && <Button className={'ai-btn ' + className} busy={busy} onClick={run} disabled={!values.complaint && !values.cause && !values.correction}>✨ {busy ? 'Writing…' : 'Write it properly (AI)'}</Button>}
      {out && (
        <Modal title="AI wording — check it, then use it" onClose={() => setOut(null)}>
          <Form submitLabel="Use this" onCancel={() => setOut(null)} onSubmit={async v => { onApply(Object.fromEntries(keys.map(k => [k, v[k] ?? '']))); setOut(null); }}>
            <div className="ai-diff full">
              {keys.map(k => (
                <label key={k} className="field full"><span>{k[0].toUpperCase() + k.slice(1)}</span>
                  {values[k] && <small className="before">{values[k]}</small>}
                  <textarea name={k} rows={3} defaultValue={out[k] || values[k]} /></label>
              ))}
            </div>
            <p className="form-note full">AI only rewrites what you wrote — check that nothing was added or left out. You can edit before using it.</p>
          </Form>
        </Modal>
      )}
    </>
  );
}

/** ✨ next to a single line (labor description, job name): fixes spelling / translates to shop English. */
export function AiLineButton({ text, onText }: { text: string; onText: (t: string) => void }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  if (!useAiOn()) return null;
  return (
    <button type="button" className="btn ai-btn ai-mini" disabled={busy || !text.trim()} title="Fix wording with AI" onClick={async () => {
      setBusy(true);
      try { onText((await api<{ text: string }>('/api/ai/line', { body: { text } })).text); } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); }
    }}>✨ {busy ? '…' : 'Fix'}</button>
  );
}

/** Used when "AI fixes wording automatically" is on: returns the cleaned text, or the original if AI isn't available. */
export async function autoFix(text: string) {
  try { return (await api<{ text: string }>('/api/ai/line', { body: { text } })).text || text; } catch { return text; }
}
